import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/SubscriptionsService';
import {Subscription} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useSubscription = (subscriptionId?: string) => {
    const queryClient = useQueryClient();

    const subscriptionQuery = useQuery({
        queryKey: ['subscription', subscriptionId],
        queryFn: () => fetch(subscriptionId!),
        enabled: !!subscriptionId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Subscription) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            return add(payload as Subscription);
        },
        onSuccess: () => {
            notifySuccess('Subscription created');
            queryClient.invalidateQueries({queryKey: ['subscriptions']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Subscription) => {
            if (!subscriptionId) throw new Error('subscriptionId is required parameter');
            return update(subscriptionId, values);
        },
        onSuccess: () => {
            notifySuccess('Subscription updated');
            queryClient.invalidateQueries({queryKey: ['subscriptions']});
            queryClient.invalidateQueries({queryKey: ['subscription', subscriptionId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!subscriptionId) throw new Error('subscriptionId is required parameter');
            return deleteRecord(subscriptionId);
        },
        onSuccess: () => {
            notifySuccess('Subscription deleted');
            queryClient.invalidateQueries({queryKey: ['subscriptions']});
        },
        onError: notifyError,
    });

    return {
        record: subscriptionQuery.data,
        loading: subscriptionQuery.isLoading,
        fetchRecord: subscriptionQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
